import { styled } from '@mui/material/styles'
import Toolbar from '@mui/material/Toolbar'
import React, { useState, useEffect } from 'react'
import i18n from 'i18next'
import { useTranslation } from 'react-i18next'
import Grid from '@mui/material/Grid2'
import caleoLogo from 'assets/images/caleoLogoSmallBlack.svg'
import { useNavigate, useLocation } from 'react-router'
import { ITermsAndPolicy } from 'types/termsAndPolicyInterfaces'
import { termsAndPolicyApi } from 'api/TermsAndPolicyAPI'
import { chooseDBTranslation } from 'utils/translations'
import TermsAndPolicyModal from 'Components/reusable/TermsAndPolicyModal/TermsAndPolicyModal'
import { useUser } from 'hooks'
import SalesPoolModal from 'Components/General/SalesPoolModal/SalesPoolModal'
import { IError } from 'types/error'

const ActionButton = styled('button')(({ theme }) => ({
  marginRight: 10,
  cursor: 'pointer',
  color: theme.palette.neutral.main,
  fontFamily: 'Helvetica',
  fontSize: 17,
  textDecoration: 'none',
  background: 'none',
  border: 'none',
  padding: 0,

  '&:focus-visible': {
    outline: `2px solid ${theme.palette.secondary.main}`,
    outlineOffset: 2,
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: 10,
    marginRight: 5,
  },
}))

const AnchorLink = styled('a')(({ theme }) => ({
  marginRight: 10,
  cursor: 'pointer',
  color: theme.palette.neutral.main,
  fontFamily: 'Helvetica',
  fontSize: 17,
  textDecoration: 'none',

  [theme.breakpoints.down('sm')]: {
    fontSize: 10,
    marginRight: 5,
  },

  '&:focus-visible': {
    outline: `2px solid ${theme.palette.secondary.main}`,
    outlineOffset: 2,
  },
}))

const LogoButton = styled('button')(({ theme }) => ({
  marginRight: 10,
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  padding: 0,

  [theme.breakpoints.down('sm')]: {
    marginRight: 5,
  },

  '&:focus-visible': {
    outline: `2px solid ${theme.palette.secondary.main}`,
    outlineOffset: 2,
  },
}))

const LogoImage = styled('img')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    width: 80,
  },
}))

/**
 * Footer component.
 *
 * @returns Footer component.
 * @notExported
 */
const Footer: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { user, groups, features } = useUser()
  const [termsAndPolicies, setTermsAndPolicies] = useState<ITermsAndPolicy[]>([])
  const [selectedItem, setSelectedItem] = useState<ITermsAndPolicy | null | undefined>(undefined)

  useEffect(() => {
    ;(async () => {
      const controller = new AbortController()

      try {
        const termsAndPolicies = await termsAndPolicyApi.getGlobalTermsAndPolicies(controller)
        setTermsAndPolicies(termsAndPolicies)
      } catch (error) {
        const err = error as IError
        if (err.name !== 'CanceledError') {
          console.error(err)
        }
      }

      return () => {
        controller.abort()
      }
    })()
  }, [])

  const handleLogoClick = () => {
    if (groups.includes('sales')) {
      navigate('/employees')
    } else {
      navigate('/profile')
    }
  }

  let hideToolbar = false
  if (
    location.pathname.includes('/reset-password') ||
    location.pathname.includes('/forgot-password') ||
    location.pathname.includes('/change-password') ||
    location.pathname.includes('/login') ||
    location.pathname.includes('/register') ||
    location.pathname.includes('/confirm') ||
    location.pathname.includes('/activate') ||
    location.pathname.includes('/terms') ||
    location.pathname.includes('/privacy-policy')
  ) {
    hideToolbar = true
  }

  return (
    <>
      {!hideToolbar && (
        <>
          <Toolbar sx={{ height: 'auto', width: '100%' }}>
            <Grid size={12} container alignItems="baseline" direction="row">
              <Grid sx={{ marginTop: '10px', marginBottom: '10px' }} size="auto">
                <Grid size="grow">
                  {termsAndPolicies &&
                    termsAndPolicies.length > 0 &&
                    termsAndPolicies.map(item => {
                      return (
                        <ActionButton type="button" key={item.id} onClick={() => setSelectedItem(item)}>
                          {chooseDBTranslation(i18n, item).title}
                        </ActionButton>
                      )
                    })}
                  <AnchorLink href="mailto:support@caleo-rm.com">{t('footer.support')}</AnchorLink>
                </Grid>
              </Grid>
              {user && features && features.includes('salespool') && (
                <Grid size="grow">
                  <SalesPoolModal />
                </Grid>
              )}
            </Grid>
            <LogoButton type="button" aria-label="Go to homepage" onClick={handleLogoClick}>
              <LogoImage src={caleoLogo} alt="" />
            </LogoButton>
          </Toolbar>
          {selectedItem !== undefined && (
            <TermsAndPolicyModal
              item={selectedItem}
              onClose={() => setSelectedItem(undefined)}
              header={chooseDBTranslation(i18n, selectedItem)?.title}
              noEscClose
            />
          )}
        </>
      )}
    </>
  )
}

export default Footer
